import { Component, OnInit, Input, OnChanges} from '@angular/core';
import { UserPrivilegeEnum, UserService } from '@app/user.service';
import { ACApiService } from '@app/api/ac-api.service';
import { User } from '@app/api/user-api.service';
import { ActivityLog, ActivityLogApiService } from '@app/api/activity-log-api';
import { ColDef, IServerSideDatasource, GridOptions } from 'ag-grid-community';
import { AgTimeAgoPipe } from '@app/shared/pipes/ag-time-ago/ag-time-ago.pipe';

@Component({
    selector: 'activity-log-grid',
    templateUrl: './activity-log-grid.component.html',
    styleUrls: ['./activity-log-grid.component.scss']
})
export class ActivityLogGridComponent implements OnInit, OnChanges {

    @Input() public acId?: number;
    @Input() public userPrefs: any;
    @Input() public gridPage: string;
    title = 'ActivityLog';
    isLoading: boolean;
    public authUser: User;
    totalSize = 0;
    customerFieldsMap = {
        'name': 'name',
        'address_1': 'address 1',
        'address_2': 'address 2',
        'country': 'country',
        'phone': 'phone',
        'purchase_date': 'purchase date',
        'sma_expiry_date': 'SMA expiry date',
        'primary_contact': 'primary contact',
        'secondary_contact': 'secondary contact',
        'comments': 'comments'
    };
    tableData: Array<ActivityLog>;

    private agTimeAgoPipe = new AgTimeAgoPipe();
    public columnDefs: ColDef[];
    public serverSideDatasource: IServerSideDatasource;
    public authUserColumnPrefs: any;
    public categoryOptions: any;
    public canViewOtherAccounts = false;
    gridOptions: GridOptions = {
        suppressDragLeaveHidesColumns: true,
    };

    private formatDescDate: string[] = ["update_customer", "software_library"];

    constructor(
        private userService: UserService,
        private activityLogApiService: ActivityLogApiService,
        private acApiService: ACApiService
    ) {
        //
    }

    async ngOnInit() {
        this.isLoading = true;
        this.authUser = await this.userService.getAuthUser();
        this.categoryOptions = await this.activityLogApiService.getCategoryOptions(this.gridPage);

        this.canViewOtherAccounts = await this.userService.userHasPrivs(
            [UserPrivilegeEnum.VIEW_OTHER_ACCOUNTS], this.authUser.customer_id);

        this.configureColumns();
        this.configureServerSideDatasource();
    }

    async ngOnChanges(changes: any) {
        if (changes.userPrefs && !changes.userPrefs.firstChange) {
            this.userPrefs = changes.userPrefs.currentValue;
        }
      }

    private configureServerSideDatasource() {
        this.serverSideDatasource = {
            getRows: async (params) => {
                try {
                    const filterModel = params.request.filterModel;
                    if(filterModel && filterModel.created_date){
                        // Prepare dates as desired query values:
                        //  1. Convert dateFrom to UTC in ISO format
                        //  2. Set dateTo time portion to '23:59:59'
                        //  3. Convert dateTo to UTC in ISO format
                        filterModel.created_date.dateFrom = new Date(filterModel.created_date.dateFrom).toISOString();
                        filterModel.created_date.dateTo = filterModel.created_date.dateTo.replace('00:00:00', '23:59:59');
                        filterModel.created_date.dateTo = new Date(filterModel.created_date.dateTo).toISOString();
                    }

                    let resp;
                    if (!this.acId) {
                        resp = await this.activityLogApiService
                            .postActivityLogGridQuery(params.request);
                    }
                    else {
                        resp = await this.acApiService
                            .postActivityLogGridQuery(params.request, this.acId);
                    }

                    params.success({
                        rowData: resp.rows,
                        rowCount: resp.lastRow
                    });
                }
                catch (error) {
                    console.log('getRows', error);
                    params.fail();
                }
            }
        };
        this.isLoading = false;
    }

    private configureColumns() {
        this.columnDefs = [
            {
                field: 'created_date',
                headerName: 'Date/Time',
                sort: 'desc',
                filter: 'agDateColumnFilter',
                filterParams: {
                    filterOptions: ['inRange']
                },
                valueFormatter: (params) => this.formatCreatedDate(params),
                tooltipValueGetter: (params) => this.formatCreatedDate(params)
            },
            {
                field: 'user_display_name',
                headerName: 'User',
                filter: 'agTextColumnFilter',
                valueFormatter: (params) => this.formatUser(params),
                tooltipValueGetter: (params) => this.formatUser(params)
            },
            {
                field: 'description',
                headerName: 'Description',
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 200,
                valueFormatter: (params) => this.getDescription(params),
                tooltipValueGetter: (params) => this.getDescription(params)
            },
            {
                field: 'user_reason_for_action',
                headerName: 'Reason',
                filter: 'agTextColumnFilter',
                valueFormatter: (params) => this.getReason(params),
                tooltipValueGetter: (params) => this.getReason(params)
            },
            {
                field: 'category_text',
                headerName: 'Category',
                valueFormatter: (params) => this.getCategory(params),
                tooltipValueGetter: (params) => this.getCategory(params),
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: this.getCategoryOptionsText()
                }
            },
            {
                field: 'severity_level_text',
                headerName: 'Level',
                valueFormatter: (params) => this.getLogLevel(params),
                tooltipValueGetter: (params) => this.getLogLevel(params),
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: ['INFO', 'DEBUG', 'WARNING', 'ERROR']
                }
            }
        ];

        // if we are not showing activity logs for a particular CID, show the
        // customer column
        if (!this.acId) {
            this.columnDefs.push(
                {
                    field: 'customer_display_name',
                    hide: !this.canViewOtherAccounts,
                    headerName: 'Customer',
                    filter: 'agTextColumnFilter',
                    valueFormatter: (params) => this.getCustomer(params),
                    tooltipValueGetter: (params) => this.getCustomer(params)
                }
            )
        }
    }

    public isSuperuser() {
        return this.authUser && this.authUser.is_superuser;
    }

    private formatCreatedDate(params) {
        return this.agTimeAgoPipe.transform(params.data.created_date);
    }

    private formatUser(params) {
        return params.data.user_display_name;
    }

    /**
     * Finds UTC datetimes with format YYYY-MM-DDTHH:mm:ss inside the value
     * string, and replaces them with an Agilent format datetime in the
     * local time zone.
     *
     * Example:
     *      'Changed purchase date to 2022-07-01T06:00:00; changed ...'
     *  =>  'Changed purchase date to 2022-07-01 00:00:00-06:00; changed ...'
     **/
    private formatUtcDateTimesInsideString(value) {
        return value.replace(
            /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}\b/,
            match => this.agTimeAgoPipe.transform(match));
    }

    private getDescription(params) {
        // Convert UTC dates to standard Portal datetime display format
        let desc = params.data.description;
        if (this.formatDescDate.includes(params.data.category_id)) {
            desc = this.formatUtcDateTimesInsideString(desc);
        }
        return desc;
    }

    private getCategory(params) {
        return params.data.category_text;
    }

    private getLogLevel(params) {
        return params.data.severity_level_text;
    }

    private getReason(params) {
        return params.data.user_reason_for_action;
    }

    private getCustomer(params) {
        return params.data.customer_display_name;
    }

    private getCategoryOptionsText() {
        return this.categoryOptions;
    }
}